'use client';

import React from 'react';
import { CrossBVModal } from 'assets/icons/CrossBVModal';
import { CustomImage } from 'components/shared/custom-image';
import { PerformanceLabModalBodyScrollLock } from './PerformanceLabModalBodyScrollLock';
import styles from './SharedDetailModal.module.css';

export interface SharedDetailModalSection {
  title?: string;
  content: string;
}

interface SharedDetailModalProps {
  title: string;
  sections?: SharedDetailModalSection[];
  description?: string;
  benefits?: string[];
  iconSrc?: string;
  iconAlt?: string;
  showIconPlaceholder?: boolean;
  sectionImageSrc?: string;
  footer: React.ReactNode;
  onClose: () => void;
  closeAriaLabel: string;
  resolveText?: (value: string) => string;
  dialogAriaLabel?: string;
}

const resolveWith = (
  value: string,
  resolveText?: (rawValue: string) => string,
): string => (resolveText ? resolveText(value) : value);

export const SharedDetailModal: React.FC<SharedDetailModalProps> = ({
  title,
  sections,
  description,
  benefits,
  iconSrc,
  iconAlt = '',
  showIconPlaceholder,
  sectionImageSrc,
  footer,
  onClose,
  closeAriaLabel,
  resolveText,
  dialogAriaLabel,
}) => {
  const resolvedTitle = resolveWith(title, resolveText);
  const hasSections = Boolean(sections?.length);

  return (
    <PerformanceLabModalBodyScrollLock>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-label={dialogAriaLabel ?? resolvedTitle}>
        <button className={styles.closeButton} type="button" onClick={onClose} aria-label={closeAriaLabel}>
          <CrossBVModal />
        </button>

        <div className={styles.headerSection}>
          {iconSrc ? (
            <CustomImage className={styles.headerThumbnail} src={iconSrc} alt={iconAlt} width={56} height={56} />
          ) : (
            showIconPlaceholder && <div className={styles.thumbnailPlaceholder} />
          )}
          <h2 className={styles.headerTitle}>{resolvedTitle}</h2>
        </div>

        <div className={styles.modalContent} data-lenis-prevent="true">
          {hasSections ? (
            <div className={styles.sectionsContainer}>
              {sectionImageSrc && (
                <CustomImage
                  className={styles.sectionImage}
                  src={sectionImageSrc}
                  alt={resolvedTitle}
                  width={576}
                  height={324}
                />
              )}
              {sections?.map((section, idx) => (
                <div className={styles.sectionItem} key={idx}>
                  {section.title && <span className={styles.sectionTitle}>{resolveWith(section.title, resolveText)}</span>}
                  <p
                    className={styles.sectionText}
                    data-italic={idx === 0 && !section.title ? 'true' : undefined}
                  >
                    {resolveWith(section.content, resolveText)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.fallbackContent}>
              {sectionImageSrc && (
                <CustomImage
                  className={styles.sectionImage}
                  src={sectionImageSrc}
                  alt={resolvedTitle}
                  width={576}
                  height={324}
                  style={{ marginBottom: 24 }}
                />
              )}
              {description && (
                <p className={styles.descriptionText}>{resolveWith(description, resolveText)}</p>
              )}
              {Boolean(benefits?.length) && (
                <ul className={styles.benefitsList}>
                  {benefits?.map((benefit, idx) => (
                    <li className={styles.benefitItem} key={idx}>{resolveWith(benefit, resolveText)}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className={styles.footerWrapper}>
          <div className={styles.gradientFade} />
          <div className={styles.stickyFooter}>{footer}</div>
        </div>
      </div>
    </PerformanceLabModalBodyScrollLock>
  );
};
